import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Images, Cpu, Zap } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { AnimatedSection } from "@/components/shared/animations";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { countUpVariants } from "@/lib/animations";

interface TechStat {
  icon: ReactNode;
  value: string;
  label: string;
  description: string;
}

/**
 * TechnologySection - 技术底座展示区域
 * 使用 shadcn/ui Card + Vercel 黑白主题适配
 */
export function TechnologySection() {
  const { t } = useTranslation("home");
  const prefersReducedMotion = useReducedMotion();

  const stats: TechStat[] = [
    {
      icon: <Images className="w-6 h-6" />,
      value: t("technology.stats.trainingImages.value"),
      label: t("technology.stats.trainingImages.label"),
      description: t("technology.stats.trainingImages.description"),
    },
    {
      icon: <Cpu className="w-6 h-6" />,
      value: t("technology.stats.architecture.value"),
      label: t("technology.stats.architecture.label"),
      description: t("technology.stats.architecture.description"),
    },
    {
      icon: <Zap className="w-6 h-6" />,
      value: t("technology.stats.inferenceSpeed.value"),
      label: t("technology.stats.inferenceSpeed.label"),
      description: t("technology.stats.inferenceSpeed.description"),
    },
  ];

  return (
    <section className="py-12 sm:py-20 lg:py-32 bg-zinc-950 dark:bg-black relative overflow-hidden">
      {/* 背景装饰 - 抽象神经网络效果 */}
      <div className="absolute inset-0 opacity-20" aria-hidden="true">
        {/* 网格背景 */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />
        {/* 渐变光晕 */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]"
          style={{
            background:
              "radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)",
          }}
        />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
        {/* 标题 */}
        <AnimatedSection className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
            {t("technology.title")}
          </h2>
          <div className="w-12 sm:w-16 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 mx-auto rounded-full" />
        </AnimatedSection>

        {/* 统计数据 - 使用 shadcn Card */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {stats.map((stat, idx) => (
            <motion.div
              key={idx}
              initial={prefersReducedMotion ? false : "hidden"}
              whileInView="visible"
              viewport={{ once: true }}
              variants={countUpVariants}
              transition={{ delay: idx * 0.15 }}
            >
              <Card className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors">
                <CardContent className="p-4 sm:p-6 text-center">
                  {/* 图标 */}
                  <div className="w-11 h-11 sm:w-12 md:w-14 sm:h-12 md:h-14 rounded-xl sm:rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-3 sm:mb-4 text-white/60">
                    {stat.icon}
                  </div>

                  {/* 数值 */}
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1.5 sm:mb-2">
                    {stat.value}
                  </div>

                  {/* 标签 */}
                  <div className="text-xs sm:text-sm md:text-base text-white/50">
                    {stat.label}
                  </div>

                  {/* 描述 */}
                  <div className="text-xs text-white/40 mt-2 sm:mt-3 leading-relaxed">
                    {stat.description}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default TechnologySection;
